import { Item, Root } from "@radix-ui/react-toggle-group";
import { cv } from "@suse-o11y/class-variance";
import React, { useEffect, useMemo } from "react";

import type { BorgAgentInfo, BorgAgentType } from "../models/borg-types";

const AGENT_MODE_OPTIONS: ReadonlyArray<{ value: BorgAgentType; label: string }> = [
	{ value: "adaptive", label: "Adaptive" },
	{ value: "chat", label: "Chat" },
	{ value: "investigation", label: "Investigation" },
	{ value: "promql_assist", label: "PromQL" },
	{ value: "docs_assist", label: "Docs" },
];

const isAgentMode = (value?: string): value is BorgAgentType =>
	value != null && AGENT_MODE_OPTIONS.some((option) => option.value === value);

interface AgentModeOption {
	value: BorgAgentType;
	label: string;
	available: boolean;
	reason?: string;
}

/** Derives selector options from backend agent data, preserving display names. */
const deriveOptions = (agents: ReadonlyArray<BorgAgentInfo>): ReadonlyMap<BorgAgentType, AgentModeOption> => {
	const options = new Map<BorgAgentType, AgentModeOption>();

	for (const agent of agents) {
		if (!options.has(agent.type)) {
			options.set(agent.type, {
				value: agent.type,
				label: agent.display_name,
				available: agent.status === "active",
				reason: agent.reason,
			});
		}
	}

	return options;
};

const pillVariants = cv("cursor-pointer rounded-full border px-3 py-1.5 font-medium text-xs transition-colors", {
	variants: {
		appearance: {
			"selected-adaptive": "border-primary-600 bg-primary-600 text-white shadow-sm",
			selected: "border-primary-600 bg-primary-600 text-white shadow-sm",
			"unselected-adaptive": "border-grey-400 bg-white text-grey-800 hover:border-primary-500 hover:text-primary-500",
			unselected: "border-grey-400 bg-white text-grey-800 hover:border-primary-500 hover:text-primary-500",
			unavailable: "cursor-not-allowed border-grey-300 bg-grey-100 text-grey-500",
		},
	},
});

const pillAppearance = (value: BorgAgentType, isSelected: boolean, isAvailable: boolean) => {
	if (!isAvailable) {
		return "unavailable" as const;
	}
	if (isSelected) {
		return value === "adaptive" ? ("selected-adaptive" as const) : ("selected" as const);
	}
	return value === "adaptive" ? ("unselected-adaptive" as const) : ("unselected" as const);
};

export const AgentModeSelector: React.FC<{
	selected: BorgAgentType;
	onChange: (mode: BorgAgentType) => void;
	agents?: ReadonlyArray<BorgAgentInfo>;
}> = ({ selected, onChange, agents }) => {
	const options = useMemo(() => (agents == null ? null : deriveOptions(agents)), [agents]);

	// If selected agent becomes unavailable, fall back to adaptive
	useEffect(() => {
		if (options == null) {
			return;
		}
		const current = options.get(selected);
		if (current != null && !current.available) {
			onChange("adaptive");
		}
	}, [options, selected, onChange]);

	if (options != null && options.size > 0) {
		return (
			<Root
				aria-label="Agent mode"
				className="flex flex-wrap gap-1.5"
				onValueChange={(val) => {
					if (isAgentMode(val)) {
						onChange(val);
					}
				}}
				type="single"
				value={selected}
			>
				{[...options.values()].map(({ value, label, available, reason }) => (
					<Item
						className={pillVariants({
							className: undefined,
							appearance: pillAppearance(value, selected === value, available),
						})}
						disabled={!available}
						key={value}
						title={available ? undefined : (reason ?? "Agent unavailable")}
						value={value}
					>
						{label}
					</Item>
				))}
			</Root>
		);
	}

	// Fallback: static options when no backend data is available
	return (
		<Root
			aria-label="Agent mode"
			className="flex flex-wrap gap-1.5"
			onValueChange={(val) => {
				if (isAgentMode(val)) {
					onChange(val);
				}
			}}
			type="single"
			value={selected}
		>
			{AGENT_MODE_OPTIONS.map(({ value, label }) => (
				<Item
					className={pillVariants({
						className: undefined,
						appearance: pillAppearance(value, selected === value, true),
					})}
					key={value}
					value={value}
				>
					{label}
				</Item>
			))}
		</Root>
	);
};
